/**
 * Created: 2026-08-03
 * Initial commit: Adapt startEvent to the competitors-based mutation input
 *
 * Builds the `competitors` input for the `startEvent` mutation from a
 * seeding-ordered player list. For `DOUBLES`, consecutive pairs of players
 * (by seeding position) become a fixed team.
 */

import { GameFormat } from './types'

// Matches the shape of the generated `CompetitorInput` mutation input type
// (player1Id/player2Id as plain UUID strings), without depending on it.
export type CompetitorInput = {
  player1Id: string
  player2Id?: string | null
}

export function toCompetitors<T extends { user: { id?: string | null } }>(
  players: T[],
  gameFormat: GameFormat,
): CompetitorInput[] {
  const ids = players.flatMap((p) => (p.user.id ? [p.user.id] : []))

  if (gameFormat === GameFormat.Doubles) {
    const competitors: CompetitorInput[] = []
    for (let i = 0; i < ids.length; i += 2) {
      competitors.push({ player1Id: ids[i], player2Id: ids[i + 1] })
    }
    return competitors
  }

  return ids.map((id) => ({ player1Id: id }))
}

/**
 * Groups a seeding-ordered player list into a `PlannerEvent`-shaped `competitors`
 * list, for previewing the match plan before `startEvent` is actually called. For
 * `DOUBLES`, consecutive pairs of players (by seeding position) become a fixed team,
 * mirroring `toCompetitors()`'s pairing for the mutation input.
 */
export function toPreviewCompetitors<T extends { id: string; user: unknown }>(
  players: T[],
  gameFormat: GameFormat,
): { id: string; player1: T['user']; player2: T['user'] | null }[] {
  if (gameFormat === GameFormat.Doubles) {
    const competitors: { id: string; player1: T['user']; player2: T['user'] | null }[] =
      []
    for (let i = 0; i < players.length; i += 2) {
      competitors.push({
        id: players[i].id,
        player1: players[i].user,
        player2: players[i + 1] ? players[i + 1].user : null,
      })
    }
    return competitors
  }

  return players.map((p) => ({ id: p.id, player1: p.user, player2: null }))
}

/**
 * Flattens `Event.competitors` into a seeding-ordered list of individual players
 * (player1, then player2 if the entry is a fixed `DOUBLES` pair). Seeding position
 * (1-indexed) is the entry's index in the returned array plus one.
 *
 * Each entry also carries `competitorIndex`: the competitor's 1-based position in
 * the input `competitors` array. For Singles/RotatingDoubles/AustralianDoubles each
 * competitor is a single player, so competitorIndex equals the seeding number; for
 * fixed-partner Doubles, both players of a team share the same competitorIndex,
 * matching the team number assigned in TeamPicker.
 */
export function expandCompetitors<
  C extends { player1: unknown; player2?: unknown | null },
>(
  competitors: C[],
): { competitor: C; player: C['player1']; competitorIndex: number }[] {
  const result: { competitor: C; player: C['player1']; competitorIndex: number }[] = []
  competitors.forEach((competitor, idx) => {
    const competitorIndex = idx + 1
    result.push({ competitor, player: competitor.player1, competitorIndex })
    if (competitor.player2) {
      result.push({
        competitor,
        player: competitor.player2 as C['player1'],
        competitorIndex,
      })
    }
  })
  return result
}

/**
 * Reduces a ranked list of individual seedings down to one entry per competitor
 * (team), in rank order. A fixed-partner team's two seedings are always adjacent
 * in the ranked output, so keeping only the first occurrence of each competitor
 * naturally collapses a team to a single row.
 */
export function competitorsInRankOrder<
  C extends { id: string; player1: unknown; player2?: unknown | null },
>(seedings: number[], competitors: C[]): C[] {
  const expanded = expandCompetitors(competitors)
  const seen = new Set<string>()
  const result: C[] = []
  for (const seeding of seedings) {
    const { competitor } = expanded[seeding - 1]
    if (seen.has(competitor.id)) continue
    seen.add(competitor.id)
    result.push(competitor)
  }
  return result
}
